import { AspectRatioIcon } from '../../common/AspectRatioIcon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, SquareDashed } from 'lucide-react';
import { useCreditRates } from '../../../features/organizations/hooks';
import { useGenerationModels } from '../../../features/generation-models/hooks';
import type { GenerationModel } from '../../../features/generation-models/api';
import {
  DEFAULT_VIDEO_MODEL,
  isAudioInvariantVideoFamily,
  resolveVideoResourceType,
  formatVideoResolutionLabel,
  videoResolutionSortValue,
  RESOURCE_TYPE_VIDEO_WITH_AUDIO,
  RESOURCE_TYPE_VIDEO_WITHOUT_AUDIO,
} from '../state/useGenerationState';
import type { VideoSettings } from '../state/useGenerationState';
export type { VideoSettings } from '../state/useGenerationState';
import styles from './ScenarioGenerator.module.css';
import { useToast } from '../../../hooks/useToast';
import { getAspectRatioLabel } from '../../../utils/aspectRatio';
import { calculateAspectRatio } from '../../materials/shared/materialUtils';
import { RecommendedBadge } from '../../common/RecommendedBadge';
import { VideoModelFamilySelector } from '../common/VideoModelFamilySelector';
import '../../../styles/form-controls.css';
import '../../../styles/generation-common.css';
import { ThreeDotsIcon } from '../../common/selectIcons';

interface VideoGenerationSettingsProps {
  settings: VideoSettings;
  onSettingsChange: (settings: VideoSettings) => void;
  disabled?: boolean;
  showResizeMode?: boolean; // 最初のフレームがある場合のみ表示
  isScenarioMode?: boolean; // シナリオモードかどうか（trueの場合、durationを非表示）
  // 参照画像モードかどうか。2026-08-30 に Veo 固有の 8 秒固定ロック撤去に伴いこのコンポーネント
  // 内では未使用になったが、呼び出し側の意図を示す props として引き続き受け取れるようにしておく
  // (useEstimatedVideoCredits の第2引数として渡す呼び出し元もある)。
  isReferenceImageMode?: boolean;
  hideVideoModelSelector?: boolean; // 動画モデル選択ブロックを非表示にする（呼び出し側が別途提供する場合）
  /** 全編モーション (シナリオ設定「モーショングラフィックス」= 全編)。動画 AI を使わずコードで描画するため、
   *  動画モデルは選べなくし (薄く表示 + 理由のツールチップ)、解像度は実際に効く 1080p / 720p の 2 択、
   *  アスペクト比はモデルの対応値域に縛らず全モデルの和集合を出す。 */
  motionEngine?: boolean;
  // 参考画像 / ストーリーボードモードのとき true。r2v / mmref を持たないモデル
  // (Veo Lite 等) を選択不可にする。
  requireReferenceSupport?: boolean;
  referenceModeLabel?: string;
  /** video-to-video の参照動画 (「動画そのまま」モード) が選択されているか。
   *  true のときアスペクト比を参照動画に連動ロックし、モデルが対応していれば
   *  動画の長さの初期値を「自動」(-1) にする。 */
  hasReferenceVideo?: boolean;
  /** video-to-video の参照動画の尺(秒)。指定すると動画の長さをこの値に連動・ロックする。 */
  referenceVideoDurationSeconds?: number | null;
  /** video-to-video の参照動画の幅/高さ。出力解像度を最近傍の対応解像度へ連動させる。 */
  referenceVideoWidth?: number | null;
  referenceVideoHeight?: number | null;
  /** video-to-video の参照動画のアスペクト比 ('16:9' 等)。対応比率にあれば出力比率を連動させる。
   *  null のときは width/height から算出する。 */
  referenceVideoAspectRatio?: string | null;
}

/** アスペクト比 → アイコン SVG パス (mask で塗るので currentColor が active 色に効く)。
 *  未掲載の比率はテキスト表示にフォールバック。 */


/** プライマリ表示するアスペクト比。それ以外はドロップダウンに集約。 */
const PRIMARY_ASPECT_RATIOS = ['16:9', '9:16'];

/** セカンダリ (ドロップダウン) の表示順序。1:1, 4:3, 3:4, 21:9, 自動 の順。 */
const SECONDARY_ASPECT_ORDER = ['1:1', '4:3', '3:4', '21:9', 'adaptive'];

/** アスペクト比の表示用ラベル (adaptive → 自動)。 */
function aspectRatioDisplay(ratio: string): string {
  return ratio === 'adaptive' ? '自動' : ratio;
}

/** 比率アイコン。adaptive (自動) は比率が決まらないので点線の四角で示す。 */
function RatioIcon({ ratio, size }: { ratio: string; size?: number }) {
  return ratio === 'adaptive'
    ? <SquareDashed size={size ?? 24} aria-label="自動" />
    : <AspectRatioIcon ratio={ratio} size={size} />;
}

/**
 * credit_rate_configからモデル×解像度×音声に応じた秒あたりクレジットレートを取得する。
 * 詳細 resource_type を優先し、見つからなければファミリー別レガシー
 * (veo-with-audio / veo-without-audio / seedance) にフォールバック。
 */
export function getVideoCreditRatePerSecond(
  creditRates: Array<{ service_type: string; resource_type: string; credits_per_unit: number }> | undefined,
  videoModel: string,
  resolution: string,
  generateAudio: boolean,
  hasReferenceVideo = false,
): number {
  if (!creditRates) return 200;

  const resourceType = resolveVideoResourceType(videoModel, resolution, generateAudio, hasReferenceVideo);
  const rate = creditRates.find(
    (r) => r.service_type === 'video_generation' && r.resource_type === resourceType
  );
  if (rate) return rate.credits_per_unit;

  // video-to-video レート未登録時は非 V2V レートにフォールバック (backend と同じ挙動)
  if (hasReferenceVideo) {
    const baseType = resolveVideoResourceType(videoModel, resolution, generateAudio, false);
    const baseRate = creditRates.find(
      (r) => r.service_type === 'video_generation' && r.resource_type === baseType
    );
    if (baseRate) return baseRate.credits_per_unit;
  }

  // フォールバック: ファミリー別レガシー (backend.credit_service._resolve_legacy_video_resource_type と同期)
  const isOmniFamily = (videoModel || '').toLowerCase().startsWith('omni')
    || (videoModel || '').toLowerCase().startsWith('gemini-omni');
  const legacyType = isOmniFamily
    ? 'gemini-omni-flash'
    : isAudioInvariantVideoFamily(videoModel)
      ? 'seedance'
      : (generateAudio ? RESOURCE_TYPE_VIDEO_WITH_AUDIO : RESOURCE_TYPE_VIDEO_WITHOUT_AUDIO);
  const legacyRate = creditRates.find(
    (r) => r.service_type === 'video_generation' && r.resource_type === legacyType
  );
  return legacyRate ? legacyRate.credits_per_unit : 200;
}

/**
 * このモデル×解像度×音声の組み合わせに、v2v (参照動画) 専用レートが実登録されているか。
 * 「前の動画とつなげる」の v2v 合算課金見積り (link-previous-video-multi-model-plan.md §5.3)
 * が、対応表明済みだが v2v レート未登録のモデル (extend など) で誤って発動しないための
 * ガード。getVideoCreditRatePerSecond は v2v レート未登録時に通常レートへ静かに
 * フォールバックするため、その判別だけを行う軽量ヘルパー。
 */
export function hasVideoV2vRate(
  creditRates: Array<{ service_type: string; resource_type: string; credits_per_unit: number }> | undefined,
  videoModel: string,
  resolution: string,
  generateAudio: boolean,
): boolean {
  if (!creditRates) return false;
  const resourceType = resolveVideoResourceType(videoModel, resolution, generateAudio, true);
  return creditRates.some(
    (r) => r.service_type === 'video_generation' && r.resource_type === resourceType
  );
}

/** 全編モーション時の解像度の選択肢。モーションの描画は長辺 1080 固定 (MOTION_OUTPUT_DIMENSIONS) で、動画エディタの
 *  書き出しも '720' 以外はすべて短辺 1080 として扱う (scenarios/download/xmlShared.ts getResolution) ため、
 *  実際に効くのは 1080p / 720p の 2 つだけ。動画モデルの和集合 (4K / 2K / 480p 等) は選べても効かないので出さない。
 *  4K 対応 (描画寸法の引数化 + 書き出し経路) は後日 (2026-10-29 利用者決定)。 */
export const MOTION_ENGINE_RESOLUTIONS: readonly string[] = ['1080', '720'];

export function motionEngineResolutions(): string[] {
  return [...MOTION_ENGINE_RESOLUTIONS];
}

/** 全編モーション時のアスペクト比の選択肢: 全モデルの supported_aspect_ratios の和集合。
 *  「自動」(adaptive) は参照動画に追従する値なので除く。並びはプライマリ → 規定順 → その他。 */
export function motionEngineAspectRatios(models: ReadonlyArray<Pick<GenerationModel, 'supported_aspect_ratios'>>): string[] {
  const set = new Set<string>();
  for (const m of models) for (const r of m.supported_aspect_ratios ?? []) if (r !== 'adaptive') set.add(r);
  if (set.size === 0) return ['16:9', '9:16'];
  const order = [...PRIMARY_ASPECT_RATIOS, ...SECONDARY_ASPECT_ORDER];
  const ordered = order.filter((r) => set.has(r));
  const rest = [...set].filter((r) => !order.includes(r));
  return [...ordered, ...rest];
}

const MOTION_ENGINE_MODEL_NOTE = '全編モーションでは動画AIを使わず、コードで描画するため動画モデルは使いません';

export function VideoGenerationSettings({
  settings,
  onSettingsChange,
  disabled = false,
  showResizeMode = true,
  isScenarioMode = false,
  hideVideoModelSelector = false,
  motionEngine = false,
  requireReferenceSupport = false,
  referenceModeLabel = '参考画像',
  hasReferenceVideo = false,
  referenceVideoDurationSeconds = null,
  referenceVideoWidth = null,
  referenceVideoHeight = null,
  referenceVideoAspectRatio = null,
}: VideoGenerationSettingsProps) {
  const { data: videoModels = [] } = useGenerationModels('video');
  const toast = useToast();
  // effect (deps は linked 値のみ) から最新の toast を参照するための ref
  const toastRef = useRef(toast);
  toastRef.current = toast;

  // 現在選択中のモデル行 (DB 由来)。一覧未取得時や該当なしの場合は undefined。
  const currentModel = useMemo(
    () => videoModels.find((m) => m.model_code === settings.videoModel),
    [videoModels, settings.videoModel],
  );

  // 選択中モデルが一覧に存在しない場合は DEFAULT に戻す
  // (例: feature_flag で除外された / 別 partner で表示不可)
  useEffect(() => {
    if (videoModels.length === 0) return;
    const exists = videoModels.some((m) => m.model_code === settings.videoModel);
    if (!exists) {
      const fallback = videoModels.find((m) => m.is_default)?.model_code
        ?? videoModels[0]?.model_code
        ?? DEFAULT_VIDEO_MODEL;
      if (fallback !== settings.videoModel) {
        onSettingsChange({ ...settings, videoModel: fallback });
      }
    }
    // settings 全体を依存に入れると無限ループの恐れがあるので videoModel のみ監視
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoModels, settings.videoModel]);

  // モデルがサポートしないモードの注記 (Veo Lite の参考画像不可など)
  const unsupportedNote = useMemo(() => {
    const modes = currentModel?.supported_modes;
    if (!modes) return null;
    if (!modes.includes('r2v') && !modes.includes('mmref')) {
      return '参考画像から動画生成（AIおまかせ）は利用できません';
    }
    return null;
  }, [currentModel]);

  // モデルが許容する値域 (DB 由来)。fallback は Veo の既存値域。
  // 解像度は降順 (1080 → 720 → 480) で表示する。
  // 全編モーションは動画モデルを使わないので、選択中モデルではなく全モデルの和集合を出す。
  const availableResolutions = useMemo<string[]>(() => {
    if (motionEngine) return motionEngineResolutions();
    const list = currentModel?.supported_resolutions;
    const base = Array.isArray(list) && list.length > 0 ? list : ['720', '1080'];
    return [...base].sort((a, b) => videoResolutionSortValue(b) - videoResolutionSortValue(a));
  }, [currentModel, motionEngine, videoModels]);

  const availableAspectRatios = useMemo<string[]>(() => {
    if (motionEngine) return motionEngineAspectRatios(videoModels);
    const list = currentModel?.supported_aspect_ratios;
    if (Array.isArray(list) && list.length > 0) return list;
    return ['16:9', '9:16'];
  }, [currentModel, motionEngine, videoModels]);

  const availableDurations = useMemo<number[]>(() => {
    const list = currentModel?.supported_durations;
    const base = Array.isArray(list) && list.length > 0 ? list : [4, 5, 6, 7, 8, 9, 10];
    // 「自動」(-1) は選択肢の先頭に置く (それ以外は昇順)
    return [...base].sort((a, b) => (a === -1 ? -1 : b === -1 ? 1 : a - b));
  }, [currentModel]);

  // video-to-video: 「動画の長さ」の初期値を参照動画に連動させる (ロックはしない)。
  // モデルが「自動」(-1) をサポートする場合は -1 を初期値にする。BytePlus 2.5 の
  // 動画編集タスク (編集指示プロンプトで自動分類) は duration=-1 のみ受理のため。
  // 延長等で尺を指定したい場合はユーザーが選び直せる (ソフト連動)。
  // -1 非対応モデルは従来どおり参照動画の尺を availableDurations の正の値域へ
  // 最近傍でクランプ。複数の参照動画がある場合は最長の尺を基準にする。
  const linkedVideoDuration = useMemo<number | null>(() => {
    if (hasReferenceVideo && availableDurations.includes(-1)) return -1;
    if (referenceVideoDurationSeconds == null || !(referenceVideoDurationSeconds > 0)) return null;
    const positives = availableDurations.filter((d) => d > 0);
    if (positives.length === 0) return null;
    const target = Math.round(referenceVideoDurationSeconds);
    return positives.reduce(
      (best, d) => (Math.abs(d - target) < Math.abs(best - target) ? d : best),
      positives[0],
    );
  }, [hasReferenceVideo, referenceVideoDurationSeconds, availableDurations]);

  // リサイズモード (pad/crop) は Veo が最初のフレーム利用時のみ解釈する API パラメータ
  // (backend veo_provider の has_first_frame 分岐)。Seedance / Kling / Gemini Omni では
  // 無視されるため、Veo 選択時のみ表示する。provider が未設定の古い行は model_code で判定。
  const isVeoModel = currentModel?.provider === 'veo' || (settings.videoModel || '').startsWith('veo');

  // === video-to-video: 参照動画に出力設定 (解像度/アスペクト比/音声) を連動させる ===
  // 尺の連動 (linkedVideoDuration) と同じくソフト連動: 連動値が変わったときだけ初期値として
  // 反映し、その後のユーザーの手動変更は上書きしない。
  // 解像度: 参照動画の short side (min(w,h)) を対応解像度へ最近傍スナップ。
  const linkedResolution = useMemo<string | null>(() => {
    const w = referenceVideoWidth;
    const h = referenceVideoHeight;
    if (!w || !h || w <= 0 || h <= 0) return null;
    const nums = availableResolutions.map(Number).filter((n) => n > 0);
    if (nums.length === 0) return null;
    const tier = Math.min(w, h);
    return String(nums.reduce((best, n) => (Math.abs(n - tier) < Math.abs(best - tier) ? n : best), nums[0]));
  }, [referenceVideoWidth, referenceVideoHeight, availableResolutions]);

  // アスペクト比: 明示値 or width/height からの算出値が対応比率に含まれるときのみ連動。
  const linkedAspectRatio = useMemo<string | null>(() => {
    const ar = referenceVideoAspectRatio || calculateAspectRatio(referenceVideoWidth, referenceVideoHeight);
    if (!ar) return null;
    return availableAspectRatios.includes(ar) ? ar : null;
  }, [referenceVideoAspectRatio, referenceVideoWidth, referenceVideoHeight, availableAspectRatios]);

  // 音声生成は常時オン (DEFAULT_VIDEO_SETTINGS.generateAudio) のため、参照動画・参照音声
  // からの音声連動は行わない (2026-09-14 にトグルごと撤廃)。

  // 参照動画選択中はアスペクト比を UI から変更できないようロックする
  // (出力比率は参照動画に追従するため。BytePlus 2.5 は backend でも adaptive を強制)。
  const aspectRatioLocked = hasReferenceVideo;

  // 連動値が変わったとき (参照動画の追加/差し替え時) だけ、尺・解像度・比率を
  // まとめて 1 回の onSettingsChange で反映する。
  // ※ 項目ごとに effect を分けると、各 effect が同じ古い settings クロージャを使って
  //   onSettingsChange({ ...settings, X }) を呼ぶため、後発の effect が先発の変更を
  //   巻き戻してしまう (尺が連動しないリグレッションの原因)。必ず 1 本に統合すること。
  // deps は linked 値のみ → その後のユーザーの手動変更は上書きしない (ソフト連動)。
  useEffect(() => {
    const fixes: Partial<VideoSettings> = {};
    if (linkedVideoDuration != null && settings.duration !== linkedVideoDuration) fixes.duration = linkedVideoDuration;
    if (linkedResolution != null && settings.resolution !== linkedResolution) fixes.resolution = linkedResolution;
    if (linkedAspectRatio != null && settings.aspectRatio !== linkedAspectRatio) fixes.aspectRatio = linkedAspectRatio;
    if (Object.keys(fixes).length > 0) onSettingsChange({ ...settings, ...fixes });
    // UI 設定と異なる比率の動画が選択された場合はトーストで変更を知らせる
    // (比率 UI はロック中のため、暗黙の変更に気づけるようにする)。
    if (fixes.aspectRatio != null) {
      toastRef.current.show(`参照動画に合わせてアスペクト比を「${aspectRatioDisplay(fixes.aspectRatio)}」に変更しました`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedVideoDuration, linkedResolution, linkedAspectRatio]);

  // アスペクト比は 16:9 / 9:16 をプライマリボタン、それ以外をドロップダウンに集約する。
  const primaryAspectRatios = useMemo<string[]>(
    () => PRIMARY_ASPECT_RATIOS.filter((r) => availableAspectRatios.includes(r)),
    [availableAspectRatios],
  );
  const secondaryAspectRatios = useMemo<string[]>(() => {
    const list = availableAspectRatios.filter((r) => !PRIMARY_ASPECT_RATIOS.includes(r));
    // 規定順序 (1:1, 4:3, 3:4, 21:9, 自動) でソートし、想定外値は末尾に
    const ordered = SECONDARY_ASPECT_ORDER.filter((r) => list.includes(r));
    const rest = list.filter((r) => !SECONDARY_ASPECT_ORDER.includes(r));
    return [...ordered, ...rest];
  }, [availableAspectRatios]);
  const isSecondaryRatioActive = secondaryAspectRatios.includes(settings.aspectRatio);

  // 詳細設定エキスパンド (音声生成 / リサイズモードを格納)。既定は閉じる。
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [showAspectMore, setShowAspectMore] = useState(false);
  const aspectMoreRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showAspectMore) return;
    const handler = (e: MouseEvent) => {
      if (aspectMoreRef.current && !aspectMoreRef.current.contains(e.target as Node)) {
        setShowAspectMore(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAspectMore]);

  // モデル切替時に supported_* に含まれない値を有効値へ正規化する
  useEffect(() => {
    if (!currentModel) return;
    const fixes: Partial<VideoSettings> = {};
    if (!availableResolutions.includes(settings.resolution)) {
      // 既定は 720p 優先 (2026-08-18 ユーザー決定: 1080p は高コストのため、
      // 非対応値からのスナップ先を 1080p → 720p に変更)。
      // 720p を持たない MiniMax H3 は 768p を優先する (推奨バッジと整合。
      // フォールバック先頭は降順ソートで 2K = 高コスト側になるため)。
      fixes.resolution = availableResolutions.includes('720') ? '720'
        : availableResolutions.includes('768') ? '768'
        : availableResolutions[0];
    }
    if (!availableAspectRatios.includes(settings.aspectRatio)) {
      fixes.aspectRatio = availableAspectRatios.includes('16:9') ? '16:9' : availableAspectRatios[0];
    }
    // duration も他の設定と同様に supported_durations で正規化する。
    // -1 (AI 自動) は対応モデルなら availableDurations に含まれるため特別扱い不要。
    // 旧実装は -1 を無条件に免除していたため、自動対応モデル (Seedance) から
    // 非対応モデル (MiniMax H3 / Gemini Omni 等) へ切り替えると -1 が残り、
    // 「~」付きバッジと最大値注記が誤表示されていた (2026-08-10 修正)。
    if (!availableDurations.includes(settings.duration)) {
      fixes.duration = availableDurations.includes(4) ? 4 : availableDurations.filter((d) => d > 0)[0] ?? 4;
    }
    // 音声生成は常時オン。トグル撤廃前の state (false) が残っていれば true に戻す。
    if (!settings.generateAudio) {
      fixes.generateAudio = true;
    }
    if (Object.keys(fixes).length > 0) {
      onSettingsChange({ ...settings, ...fixes });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentModel?.model_code, availableResolutions, availableAspectRatios, availableDurations]);

  return (
    <>
      {/* 動画モデル選択 (プロバイダ別にグループ化) */}
      {!hideVideoModelSelector && videoModels.length > 0 && (
        <div className="gen-option-group" style={{ marginBottom: '4px' }}>
          <div className="gen-option-group-label">動画モデル</div>
          <VideoModelFamilySelector
            models={videoModels}
            activeModelCode={settings.videoModel}
            onSelect={(code) => onSettingsChange({ ...settings, videoModel: code })}
            disabled={disabled}
            disabledReason={motionEngine ? MOTION_ENGINE_MODEL_NOTE : undefined}
            requireReferenceSupport={requireReferenceSupport}
            referenceModeLabel={referenceModeLabel}
          />
          {/* モデル説明はボタンの hover ツールチップ側に移動済み。
              ここにはモード非対応の注記だけを残す (選択中モデル固有の制約なので常時表示) */}
          {motionEngine && (
            <span style={{ fontSize: '0.7rem', color: 'rgba(245, 241, 230, 0.45)', marginTop: '4px', display: 'block' }}>
              {MOTION_ENGINE_MODEL_NOTE}
            </span>
          )}
          {!motionEngine && unsupportedNote && (
            <span style={{ fontSize: '0.7rem', color: 'rgba(245, 241, 230, 0.45)', marginTop: '4px', display: 'block' }}>
              {unsupportedNote}
            </span>
          )}
        </div>
      )}

      <div className="form-grid">
        <div className="gen-option-group">
          <div className="gen-option-group-label">解像度</div>
          <div className={styles.resolutionButtons}>
            {availableResolutions.map((res) => (
              <button
                key={res}
                type="button"
                className={`${styles.resolutionButton} ${settings.resolution === res ? styles.resolutionButtonActive : ''}`}
                onClick={() => onSettingsChange({ ...settings, resolution: res })}
                disabled={disabled}
              >
                {/* 推奨は 720p (2026-08-18: 既定720p化に合わせて 1080p から移動)。
                    720p を持たない MiniMax H3 では相当画質で低コストな 768p を推奨する。 */}
                {(res === '720' || res === '768') && <RecommendedBadge active={settings.resolution === res} />}
                {formatVideoResolutionLabel(res)}
              </button>
            ))}
          </div>
        </div>
        <div className="gen-option-group">
          <div className="gen-option-group-label">アスペクト比</div>
          <div className={styles.aspectRatioButtons}>
            {primaryAspectRatios.map((ratio) => {
              const isActive = settings.aspectRatio === ratio;
              return (
                <button
                  key={ratio}
                  type="button"
                  className={`${styles.aspectRatioButton} ${isActive ? styles.aspectRatioButtonActive : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSettingsChange({ ...settings, aspectRatio: ratio });
                  }}
                  disabled={disabled || aspectRatioLocked}
                  title={aspectRatioLocked ? '参照動画に合わせて自動設定されます' : getAspectRatioLabel(ratio)}
                >
                  <AspectRatioIcon ratio={ratio} />
                </button>
              );
            })}
            {secondaryAspectRatios.length > 0 && (
              <div ref={aspectMoreRef} style={{ position: 'relative', display: 'inline-flex' }}>
                <button
                  type="button"
                  className={`${styles.aspectRatioButton} ${isSecondaryRatioActive ? `${styles.aspectRatioButtonActive} ${styles.aspectRatioButtonWithChevron}` : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowAspectMore((v) => !v);
                  }}
                  disabled={disabled || aspectRatioLocked}
                  title={isSecondaryRatioActive
                    ? `その他のアスペクト比 (現在: ${aspectRatioDisplay(settings.aspectRatio)})`
                    : 'その他のアスペクト比'}
                  aria-haspopup="true"
                  aria-expanded={showAspectMore}
                >
                  {isSecondaryRatioActive
                    // 三点が比率アイコンに置き換わるので、シェブロンでメニューを開けることを示す
                    ? <><RatioIcon ratio={settings.aspectRatio} size={36} /><ChevronDown size={12} aria-hidden="true" /></>
                    : <ThreeDotsIcon />}
                </button>
                {showAspectMore && (
                  <div
                    role="menu"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: 4,
                      background: 'rgba(30, 28, 24, 0.98)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: 6,
                      padding: 4,
                      zIndex: 100,
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 96,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                    }}
                  >
                    {secondaryAspectRatios.map((ratio) => {
                      const isActive = settings.aspectRatio === ratio;
                      return (
                        <button
                          key={ratio}
                          type="button"
                          role="menuitemradio"
                          aria-checked={isActive}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onSettingsChange({ ...settings, aspectRatio: ratio });
                            setShowAspectMore(false);
                          }}
                          style={{
                            background: isActive ? 'rgba(245, 241, 230, 0.12)' : 'transparent',
                            color: 'rgba(245, 241, 230, 0.9)',
                            border: 'none',
                            padding: '6px 10px',
                            textAlign: 'left',
                            fontSize: '0.78rem',
                            cursor: 'pointer',
                            borderRadius: 4,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                          title={getAspectRatioLabel(ratio)}
                        >
                          <RatioIcon ratio={ratio} size={20} />
                          {aspectRatioDisplay(ratio)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          {aspectRatioLocked && (
            <span
              style={{
                fontSize: '0.72rem',
                fontWeight: 400,
                color: 'rgba(245, 241, 230, 0.45)',
                marginTop: '4px',
                display: 'block',
              }}
            >
              ※ 参照動画に合わせて自動設定されます
            </span>
          )}
        </div>
      </div>
      {!isScenarioMode && (
        <div className="form-grid">
          <label className="gen-label gen-label-narrow">
            動画の長さ
            <select
              value={settings.duration}
              onChange={(e) => onSettingsChange({ ...settings, duration: Number(e.target.value) })}
              disabled={disabled}
              className="has-value"
            >
              {availableDurations.map((d) => (
                <option key={d} value={d}>{d === -1 ? '自動' : `${d}秒`}</option>
              ))}
            </select>
          </label>
        </div>
      )}
      {/* 詳細設定エキスパンド: リサイズモード (Veo が最初のフレームを使うときのみ有効) を
          格納する。表示対象がないときはエキスパンド自体を出さない。
          (音声生成トグルは 2026-09-14 に撤廃。音声は常時オン) */}
      {(() => {
        // リサイズモードは Veo + 最初のフレーム利用時のみ。
        const showResizeAdvanced = showResizeMode && isVeoModel;
        if (!showResizeAdvanced) return null;
        return (
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className={styles.storyboardAdvancedToggle}
              aria-expanded={showAdvanced}
            >
              <span className={`${styles.storyboardAdvancedToggleArrow} ${showAdvanced ? styles.storyboardAdvancedToggleArrowOpen : ''}`}>▸</span>
              詳細設定
            </button>
            {showAdvanced && (
              <div className={styles.storyboardAdvancedPanel}>
                {showResizeAdvanced && (
                  <label className="gen-label gen-label-narrow">
                    リサイズモード
                    <select
                      value={settings.resizeMode}
                      onChange={(e) => onSettingsChange({ ...settings, resizeMode: e.target.value as 'pad' | 'crop' })}
                      disabled={disabled}
                      className="has-value"
                      style={{ maxWidth: '200px' }}
                    >
                      <option value="crop">クロップ（推奨）</option>
                      <option value="pad">パディング</option>
                    </select>
                  </label>
                )}
              </div>
            )}
          </div>
        );
      })()}
    </>
  );
}

// 推定クレジットを計算するユーティリティフック（外部で使用可能）
export function useEstimatedVideoCredits(
  settings: VideoSettings,
  isReferenceImageMode = false,
  hasReferenceVideo = false,
  /** 参照動画の「合計」尺 (秒。複数本なら合算)。合算課金モデル (計画書 §4.1) の見積りにのみ使う。
   *  PromptModePanel の linkedVideoDuration 等が使う「最長」尺とは別物なので混同しないこと。 */
  referenceVideoSecondsTotal = 0,
) {
  const { data: creditRatesData } = useCreditRates();
  const { data: videoModels = [] } = useGenerationModels('video');

  return useMemo(() => {
    const rate = getVideoCreditRatePerSecond(
      creditRatesData?.rates,
      settings.videoModel,
      settings.resolution,
      settings.generateAudio,
      hasReferenceVideo,
    );
    const currentModel = videoModels.find((m) => m.model_code === settings.videoModel);

    let duration = settings.duration;
    // 「自動」(-1) はバックエンドのデポジットがモデル最大尺みなし (backend
    // normalize_billing_duration: Seedance 2.0=15秒 / 2.5=30秒) で行われるため、
    // 見積りも同じ値で計算する (実尺が短ければ生成後の精算で差分が返還される)。
    // -1 のまま掛け算すると見積りが負値になる。
    if (duration === -1) {
      const positives = (currentModel?.supported_durations ?? [])
        .filter((d): d is number => typeof d === 'number' && d > 0);
      duration = positives.length > 0 ? Math.max(...positives) : 15;
    }

    // 合算課金モデル (MiniMax H3 等。DB metadata.aggregates_reference_video_seconds=true) は
    // 参照動画の入力秒数も出力秒数に合算して課金される (計画書 §4.1)。モデル id のハードコード
    // 分岐ではなく DB metadata を単一情報源にする (backend の provider 実装と判定意図を揃える)。
    const aggregatesReferenceVideoSeconds = (currentModel?.metadata as { aggregates_reference_video_seconds?: boolean } | null | undefined)
      ?.aggregates_reference_video_seconds === true;
    const inputSeconds = hasReferenceVideo && aggregatesReferenceVideoSeconds ? referenceVideoSecondsTotal : 0;

    return (duration + inputSeconds) * rate;
  }, [
    settings.duration, settings.generateAudio, settings.videoModel, settings.resolution,
    creditRatesData, isReferenceImageMode, hasReferenceVideo, referenceVideoSecondsTotal, videoModels,
  ]);
}
